import { memo, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AiOutlineCloseCircle } from "react-icons/ai";

import {
  allCoffrets,
  COFFRET,
  deleteTask,
  appendEtiquetteRow,
  downloadFacturePdf,
} from "../../api/coffrets";
import { MemoizedTasks as Task } from "./Task.component";

import { TasksContext } from "../../context/tasks/store";
import { downloadCoffretPdf } from "../../utils/pdfClient";

import "./style.scss";

type PropsType = { coffret: COFFRET };

type ToastState = { message: string; type: "success" | "error" } | null;

// Helpers facture alignés avec le backend
const sanitizeBase = (name: string) =>
  String(name || "")
    .trim()
    .replace(/[\s/\\]+/g, "-");

const ensurePdf = (name: string) => name.replace(/\.(pdf)$/i, "") + ".pdf";

const buildFactureKey = (c: COFFRET): string => {
  const stored = String(c?.factureFilename || "").trim();
  if (stored) return stored;

  const rawNumero = String(c?.numeroFacture || "").trim();
  if (!rawNumero || rawNumero.toUpperCase() === "SANS") return "";

  return ensurePdf(sanitizeBase(rawNumero));
};

const TaskContainer = ({ coffret }: PropsType) => {
  const { setIsModalOpen, setCoffretToUpdate, setCoffrets } = useContext(TasksContext);

  const [openDeleteModel, setOpenDeleteModal] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceModalText, setInvoiceModalText] = useState(
    "Aucune facture disponible pour ce spécimen."
  );

  const [toast, setToast] = useState<ToastState>(null);
  const toastTimerRef = useRef<number | null>(null);

  const showToast = (message: string, type: "success" | "error" = "success") => {
    setToast({ message, type });
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(null), 1800);
  };

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  const handleExportOneCsv = async (one: COFFRET) => {
    try {
      console.log("[CSV] clic export unitaire", one);

      const resp = await appendEtiquetteRow(one);
      console.log("[CSV] réponse append", resp);

      if (!resp.ok) {
        console.warn("[ETIQUETTES append] FAILED:", resp.status, resp.message);
        showToast("Impossible d’ajouter le spécimen au CSV.", "error");
        return;
      }

      showToast("Le spécimen a été ajouté au CSV avec succès ✅", "success");
    } catch (err) {
      console.error("[CSV append] error:", err);
      showToast("Erreur réseau : ajout au CSV impossible.", "error");
    }
  };

  // ✅ FACTURE : download simple comme le CSV
  const handleDownloadInvoice = async (one: COFFRET) => {
    try {
      console.log("[FACTURE] clic reçu", one);

      const key = buildFactureKey(one);
      console.log("[FACTURE] key calculée =", key);

      if (!key) {
        console.warn("[FACTURE] aucune key trouvée");
        setInvoiceModalText("Aucune facture disponible pour ce spécimen.");
        setInvoiceModalOpen(true);
        return;
      }

      setInvoiceModalOpen(false);
      await downloadFacturePdf(key);
      showToast("Facture téléchargée avec succès ✅", "success");
    } catch (err: any) {
      console.error("[FACTURE download] error:", err);
      setInvoiceModalText(
        err?.message || "Impossible de télécharger la facture pour le moment."
      );
      setInvoiceModalOpen(true);
      showToast("Téléchargement de la facture impossible.", "error");
    }
  };

  const update = useCallback(
    async (c: COFFRET) => {
      setIsModalOpen();
      setCoffretToUpdate(c);
    },
    [setIsModalOpen, setCoffretToUpdate]
  );

  const deleteItem = useCallback(async () => {

  try {

    const id = String(
      coffret._id || ""
    ).trim();

    if (!id) {

      alert("ID introuvable");

      return;
    }

    console.log(
      "[DELETE] ID envoyé =",
      id
    );

    await deleteTask(id);

    setOpenDeleteModal(false);

    const res =
      await allCoffrets();

    setCoffrets(
      res?.data ?? []
    );

  } catch (err) {

    console.error(
      "[DELETE FRONT ERROR]",
      err
    );

    alert(
      "Erreur suppression"
    );
  }

}, [
  coffret._id,
  setCoffrets
]);

  return (
    <>
      {toast && (
        <div className={`toast toast--${toast.type}`} role="status" aria-live="polite">
          {toast.message}
        </div>
      )}

      {openDeleteModel && (
        <div className="modal-delete">
          <div className="modal-content">
            <AiOutlineCloseCircle className="close" onClick={() => setOpenDeleteModal(false)} />
            <div>
              Confirmer la suppression ?
              <div className="input-block">
                <input type="button" value="ANNULER" onClick={() => setOpenDeleteModal(false)} />
                <input type="button" value="SUPPRIMER" onClick={deleteItem} />
              </div>
            </div>
          </div>
        </div>
      )}

      {invoiceModalOpen && (
        <div className="modal-delete">
          <div className="modal-content">
            <AiOutlineCloseCircle className="close" onClick={() => setInvoiceModalOpen(false)} />
            <div>
              <span style={{ fontWeight: 600, marginBottom: 8, display: "block" }}>
                Facture indisponible
              </span>
              <p style={{ opacity: 0.85, marginBottom: 16 }}>{invoiceModalText}</p>
              <div className="input-block">
                <input type="button" value="OK" onClick={() => setInvoiceModalOpen(false)} />
              </div>
            </div>
          </div>
        </div>
      )}

      <Task
        coffret={coffret}
        updateTask={update}
        setOpenDeleteModal={setOpenDeleteModal}
        onExportPdf={async () => {
          try {
            setPdfBusy(true);
            await downloadCoffretPdf(coffret);
          } finally {
            setPdfBusy(false);
          }
        }}
        onDownloadInvoice={handleDownloadInvoice}
        onExportOneCsv={handleExportOneCsv}
        pdfBusy={pdfBusy}
      />
    </>
  );
};

export const MemoizedTaskContainer = memo(TaskContainer);